import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { SubscriptionHistoryItem } from '../types/api';
import { useTranslation } from '../context/LanguageContext';
import './Modal.css';

interface SubscriptionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId?: number;
  clientName?: string;
}

const SubscriptionHistoryModal: React.FC<SubscriptionHistoryModalProps> = ({
  isOpen,
  onClose,
  clientId,
  clientName
}) => {
  const { t, formatDate } = useTranslation();
  const [subscriptions, setSubscriptions] = useState<SubscriptionHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      fetchSubscriptionHistory();
    }
  }, [isOpen, clientId]);

  const fetchSubscriptionHistory = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = clientId
        ? await apiService.getClientSubscriptionHistory(clientId)
        : await apiService.getSubscriptionHistory();

      if (response.success && response.data) {
        setSubscriptions(response.data);
      } else {
        setError(response.message || t('failedToLoadSubscriptionHistory'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('errorLoadingSubscriptionHistory'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('subscriptionHistory')}{clientName ? ` - ${clientName}` : ''}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          {isLoading ? (
            <div className="loading-spinner">{t('loadingSubscriptionHistory')}</div>
          ) : subscriptions.length === 0 ? (
            <div className="no-subscription">
              <p>{t('noSubscriptionHistory')}</p>
            </div>
          ) : (
            <div className="appointments-history">
              {subscriptions.map((subscription) => (
                <div key={subscription.id} className="appointment-history-item">
                  <div className="subscription-section">
                    <h4>{t('subscriptionPeriod')}</h4>

                    <div className="detail-row">
                      <span className="detail-label">{t('eligibilityWindow')}:</span>
                      <span className="detail-value">
                        {formatDate(subscription.startDate)} - {formatDate(subscription.endDate)}
                      </span>
                    </div>

                    {subscription.actualStartDate && (
                      <div className="detail-row">
                        <span className="detail-label">{t('actualPeriod')}:</span>
                        <span className="detail-value">
                          {formatDate(subscription.actualStartDate)} - {formatDate(subscription.actualEndDate || subscription.endDate)}
                        </span>
                      </div>
                    )}

                    <div className="detail-row">
                      <span className="detail-label">{t('monthlyLimit')}:</span>
                      <span className="detail-value">{subscription.monthlyLimit} {t('sessions')}</span>
                    </div>

                    <div className="detail-row">
                      <span className="detail-label">{t('usedThisMonth')}:</span>
                      <span className="detail-value">{subscription.validAppointmentCount}</span>
                    </div>

                    <div className="detail-row">
                      <span className="detail-label">{t('attendedAppointmentCount')}:</span>
                      <span className="detail-value">{subscription.attendedAppointmentCount}</span>
                    </div>

                    <div className="detail-row">
                      <span className="detail-label">{t('createdAt')}:</span>
                      <span className="detail-value">{formatDate(subscription.createdAt)}</span>
                    </div>

                    {subscription.isFrozen && subscription.frozenAt && (
                      <>
                        <div className="detail-row">
                          <span className="detail-label">{t('frozenAt')}:</span>
                          <span className="detail-value">{formatDate(subscription.frozenAt)}</span>
                        </div>
                        {subscription.remainingDaysAtFreeze !== undefined && subscription.remainingDaysAtFreeze !== null && (
                          <div className="detail-row">
                            <span className="detail-label">{t('remainingDaysAtFreeze')}:</span>
                            <span className="detail-value">{subscription.remainingDaysAtFreeze} {t('days')}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="btn-secondary">
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionHistoryModal;
